"use client";

import React, { useState, useRef, useEffect } from "react";
import { Check, AlertCircle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type Status = "idle" | "submitting" | "success" | "error";

export default function FreeScanForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    website: "",
    honeypot: "",
  });

  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const successHeadingRef = useRef<HTMLHeadingElement>(null);

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = "Name is required.";
    if (!formData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!formData.website.trim()) {
      errors.website = "Website URL is required.";
    } else if (!formData.website.startsWith('http')) {
      errors.website = "Please include http:// or https://";
    }
    return errors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (status === "error") setStatus("idle");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setStatus("error");
      setErrorMessage("Please correct the errors below.");
      return;
    }

    setStatus("submitting");
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          service: "Free 5-Page Scan Request",
          message: `Requesting a free 5-page accessibility scan for ${formData.website}`
        }),
      });

      if (!response.ok) throw new Error("Failed to submit request.");
      setStatus("success");
    } catch (err: any) {
      setErrorMessage(err.message);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="bg-bloom-green-deep rounded-[24px] p-10 text-center animate-in fade-in zoom-in duration-500 text-white shadow-2xl shadow-bloom-green-deep/20">
        <div className="w-16 h-16 rounded-full bg-bloom-gold/20 flex items-center justify-center text-bloom-gold mx-auto mb-6">
          <Check size={32} />
        </div>
        <h2 className="font-serif text-3xl font-bold mb-4">Scan Requested</h2>
        <p className="font-sans text-white/80 max-w-sm mx-auto leading-relaxed mb-8">
          Thanks {formData.name.split(' ')[0]}! We&apos;ll run your scan and email the results to {formData.email} within 48 hours.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[32px] border border-bloom-border/60 p-8 md:p-12 shadow-2xl shadow-bloom-green-deep/5 max-w-[540px] mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="absolute left-[-9999px] h-0 opacity-0 overflow-hidden" aria-hidden="true">
          <input name="honeypot" value={formData.honeypot} onChange={handleChange} />
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-[10px] uppercase tracking-widest font-bold text-bloom-muted">Name</Label>
            <Input
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className={cn("h-14 bg-bloom-cream/20 border-bloom-border/40", fieldErrors.name && "border-red-400")}
              placeholder="Your Name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[10px] uppercase tracking-widest font-bold text-bloom-muted">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className={cn("h-14 bg-bloom-cream/20 border-bloom-border/40", fieldErrors.email && "border-red-400")}
              placeholder="you@organization.org"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="website" className="text-[10px] uppercase tracking-widest font-bold text-bloom-muted">Website to Scan</Label>
            <Input
              id="website"
              name="website"
              required
              value={formData.website}
              onChange={handleChange}
              className={cn("h-14 bg-bloom-cream/20 border-bloom-border/40", fieldErrors.website && "border-red-400")}
              placeholder="https://yourwebsite.com"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={status === "submitting"}
          className="w-full h-14 bg-bloom-gold hover:bg-bloom-gold-dark text-white font-sans font-bold uppercase tracking-widest text-xs transition-all shadow-lg rounded-xl"
        >
          {status === "submitting" ? <Loader2 className="animate-spin" /> : "Run My Free Scan"}
        </Button>
        <p className="text-center text-[10px] text-bloom-muted/60 uppercase tracking-tighter">
          No credit card required. results in 48 hours.
        </p>
      </form>
    </div>
  );
}
